/**
 * Tenant hospital branding helpers (logo / favicon / theme).
 * Branding is account-scoped; never trust a client-invented appsCode for isolation.
 */

export const DEFAULT_THEME = {
  primaryColor: "#1976D2",
  secondaryColor: "#FFAF38",
};

const HEX = /^#([0-9A-Fa-f]{6})$/;

export function normalizeHexColor(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  let value = String(raw).trim();
  if (!value.startsWith("#")) value = `#${value}`;
  if (!HEX.test(value)) return null;
  return value.toUpperCase();
}

export function resolveThemeColors(theme) {
  const primary =
    normalizeHexColor(theme?.primaryColor) || DEFAULT_THEME.primaryColor;
  const secondary =
    normalizeHexColor(theme?.secondaryColor) || DEFAULT_THEME.secondaryColor;
  return { primaryColor: primary, secondaryColor: secondary };
}

export function absoluteApiUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl) || String(pathOrUrl).startsWith("data:")) {
    return pathOrUrl;
  }
  const raw = window._env_?.REACT_APP_API_ENDPOINT || "http://localhost:9090/";
  const base = String(raw).endsWith("/") ? String(raw) : `${raw}/`;
  return `${base}${String(pathOrUrl).replace(/^\//, "")}`;
}

export function defaultLogoSrc() {
  return `/${window._env_?.COMPANY_NAME || "healthcare"}/logo.png`;
}

export function defaultFaviconHref() {
  return `/${window._env_?.COMPANY_NAME || "healthcare"}/favicon.ico`;
}

export function resolveLogoSrc({ logoUrl, companyLogo } = {}) {
  const fromUrl = absoluteApiUrl(logoUrl);
  if (fromUrl) return fromUrl;
  if (companyLogo) {
    if (String(companyLogo).startsWith("data:")) return companyLogo;
    return `data:image/png;base64,${companyLogo}`;
  }
  return defaultLogoSrc();
}

function withTabFaviconParam(url) {
  if (!url || !url.includes("/tenant/favicon") || /[?&]tab=/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}tab=64`;
}

export function resolveFaviconHref({ faviconUrl, companyFavIcon } = {}) {
  const fromUrl = absoluteApiUrl(faviconUrl);
  if (fromUrl) return withTabFaviconParam(fromUrl);
  if (companyFavIcon) {
    if (String(companyFavIcon).startsWith("data:")) return companyFavIcon;
    return `data:image/png;base64,${companyFavIcon}`;
  }
  return defaultFaviconHref();
}

export function setDocumentFavicon(href) {
  const next = href || defaultFaviconHref();
  let link = document.getElementById("dynamic-favicon");
  if (!link) {
    link = document.querySelector("link[rel='shortcut icon'], link[rel='icon']");
  }
  if (!link) {
    link = document.createElement("link");
    link.id = "dynamic-favicon";
    document.head.appendChild(link);
  }
  // Chrome uses rel=icon for the tab. shortcut icon alone stays on the first failed file.
  link.rel = "icon";
  if (link.href !== next && link.getAttribute("href") !== next) {
    link.href = next;
  }
}

export function resetDocumentFavicon() {
  setDocumentFavicon(defaultFaviconHref());
}

export function brandingSignature(userOrTenant) {
  if (!userOrTenant) return "";
  const theme = userOrTenant.theme || {};
  return [
    userOrTenant.appsCode || "",
    userOrTenant.tenantSlug || "",
    userOrTenant.logoUrl || "",
    userOrTenant.faviconUrl || "",
    theme.primaryColor || "",
    theme.secondaryColor || "",
    Boolean(userOrTenant.hasLogo),
    Boolean(userOrTenant.hasFavicon),
  ].join("|");
}
